import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import clsx from "clsx";

import { useAppSelector } from "reduxFiles/hooks";
import { RoutesType } from "types/globalTypes";
import { UserRole } from "types/userTypes";
import { BsDash } from "react-icons/bs";

const SidebarLinks = ({
  routes,
  onClose,
  onLinkClick,
}: {
  routes: RoutesType[];
  onClose?: React.MouseEventHandler<HTMLSpanElement>;
  onLinkClick?: () => void;
}): React.ReactElement => {
  const location = useLocation();
  const { t } = useTranslation();

  const { user } = useAppSelector((state) => state.auth);

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      onLinkClick?.();
    }
  };

  const currentLayout = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith("/admin")) return "/admin";
    if (path.startsWith("/company")) return "/company";
    if (path.startsWith("/organization")) return "/organization";
    if (path.startsWith("/assessment")) return "/assessment";
    return "";
  }, [location.pathname]);

  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      if (!route.show) return false;
      if (!route.roles) return route.layout === currentLayout;
      if (route.form && !user.organization?.form) return false;

      if (user?.role === UserRole.ADMIN) {
        return (
          route.layout === "/admin" && route.roles.includes(UserRole.ADMIN)
        );
      }

      if (user?.role === UserRole.USER) {
        if (route.layout === "/company") {
          return (
            route.roles.includes(UserRole.USER) && currentLayout === "/company"
          );
        }

        if (route.layout === "/organization") {
          return (
            user.organization &&
            route.roles.includes(UserRole.USER) &&
            currentLayout === "/organization"
          );
        }

        if (route.layout === "/assessment") {
          return (
            route.roles.includes(UserRole.USER) &&
            currentLayout === "/assessment"
          );
        }

        return false;
      }

      return false;
    });
  }, [routes, user, currentLayout]);

  const getDefaultOpenMenus = () => {
    return filteredRoutes
      .filter((route) =>
        route.children?.some((child) => location.pathname.includes(child.path))
      )
      .map((route) => route.name);
  };

  const [openMenus, setOpenMenus] = useState<string[]>(getDefaultOpenMenus());

  const toggleMenu = (menuName: string) => {
    setOpenMenus((prev) =>
      prev.includes(menuName)
        ? prev.filter((item) => item !== menuName)
        : [...prev, menuName]
    );
  };

  const isActiveRoute = (path: string) => {
    return location.pathname.includes(path);
  };

  const renderChildren = (route: RoutesType) => {
    if (!route.children?.length) return null;

    const isOpen = openMenus.includes(route.name);

    const filteredChildren = route.children.filter((child) => {
      if (!child.show) return false;
      if (!child.roles) {
        return child.layout === currentLayout;
      }
      if (child.needVerify && !user.organization?.needVerifyCompanies) {
        return false;
      }

      const userRole = user?.role;

      if (userRole === UserRole.ADMIN) {
        return (
          child.layout === "/admin" && child.roles.includes(UserRole.ADMIN)
        );
      }

      if (userRole === UserRole.USER) {
        if (child.layout === "/company") {
          return (
            child.roles.includes(UserRole.USER) && currentLayout === "/company"
          );
        }
        if (child.layout === "/organization") {
          return (
            user.organization &&
            child.roles.includes(UserRole.USER) &&
            currentLayout === "/organization"
          );
        }
        return false;
      }

      return false;
    });

    if (filteredChildren.length === 0) return null;

    return (
      <div
        className={clsx(
          "grid transition-all duration-300 ease-in-out",
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-2 flex flex-col gap-1 border-r border-gray-200 pr-5 dark:border-white/10">
            {filteredChildren?.map((child, childIndex) => {
              const isChildActive = isActiveRoute(child.path);

              return (
                <Link
                  key={childIndex}
                  to={`${child.layout}/${child.path}`}
                  onClick={handleLinkClick}
                >
                  <div
                    className={clsx(
                      `
                        group relative ms-2 flex items-center
                        rounded-xl px-4 py-2 transition-all duration-200
                      `,
                      isChildActive
                        ? `
                            bg-brand-500/15
                            text-brand-600
                            dark:bg-brand-500/20
                            dark:text-white
                          `
                        : `
                            text-gray-800
                            hover:bg-gray-100
                            dark:text-gray-300
                            dark:hover:bg-white/5
                          `
                    )}
                  >
                    <div
                      className={clsx(
                        `
                          absolute right-0 h-6 w-1 rounded-full
                          transition-all duration-300
                        `,
                        isChildActive ? "bg-brand-500 opacity-100" : "opacity-0"
                      )}
                    />

                    <div className="flex items-center gap-3">
                      <span
                        className={clsx(
                          isChildActive
                            ? "text-brand-500 dark:text-white"
                            : "text-gray-500 dark:text-gray-400"
                        )}
                      >
                        {child.icon || <BsDash />}
                      </span>

                      <span className="text-sm font-medium">
                        {t(`pages.${child.name}`)}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ul className="flex flex-col gap-1">
      {filteredRoutes.map((route, index) => {
        const hasChildren = !!route.children?.length;

        const isParentActive =
          isActiveRoute(route.path) ||
          route.children?.some((child) => isActiveRoute(child.path));

        const isOpen = openMenus.includes(route.name);

        if (!hasChildren && !route.component) return null;

        return (
          <li key={index}>
            {/* Parent Route */}
            {hasChildren ? (
              <button
                onClick={() => toggleMenu(route.name)}
                className={clsx(
                  `
                    group relative flex w-full items-center justify-between
                    rounded-2xl px-4 py-3
                    transition-all duration-200
                  `,
                  isParentActive
                    ? `
                        bg-brand-500/15
                        dark:bg-brand-500/20
                      `
                    : `
                        hover:bg-gray-100
                        dark:hover:bg-white/5
                      `
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={clsx(
                      isParentActive
                        ? "text-brand-600 dark:text-white"
                        : "text-gray-600 dark:text-gray-400"
                    )}
                  >
                    {route.icon || <BsDash />}
                  </span>

                  <span
                    className={clsx(
                      "text-sm font-semibold transition-colors",
                      isParentActive
                        ? "text-brand-600 dark:text-white"
                        : "text-gray-800 dark:text-white"
                    )}
                  >
                    {t(`pages.${route.name}`)}
                  </span>
                </div>

                <FiChevronDown
                  className={clsx(
                    "transition-all duration-300",
                    isOpen ? "rotate-180" : "",
                    isParentActive
                      ? "text-brand-500 dark:text-white"
                      : "text-gray-500 dark:text-gray-400"
                  )}
                />
              </button>
            ) : (
              <Link
                to={`${route.layout}/${route.path}`}
                onClick={handleLinkClick}
              >
                <div
                  className={clsx(
                    `
                      group relative flex items-center
                      rounded-2xl px-4 py-3
                      transition-all duration-200
                    `,
                    isParentActive
                      ? `
                          bg-brand-500/15
                          dark:bg-brand-500/20
                        `
                      : `
                          hover:bg-gray-100
                          dark:hover:bg-white/5
                        `
                  )}
                >
                  <div
                    className={clsx(
                      `
                        absolute right-0 h-8 w-1 rounded-full
                        transition-all duration-300
                      `,
                      isParentActive ? "bg-brand-500 opacity-100" : "opacity-0"
                    )}
                  />

                  <span
                    className={clsx(
                      isParentActive
                        ? "text-brand-600 dark:text-white"
                        : "text-gray-600 dark:text-gray-400"
                    )}
                  >
                    {route.icon || <BsDash />}
                  </span>

                  <span
                    className={clsx(
                      `
                        ms-3 text-sm font-semibold
                        transition-colors
                      `,
                      isParentActive
                        ? "text-brand-600 dark:text-white"
                        : "text-gray-800 dark:text-white"
                    )}
                  >
                    {t(`pages.${route.name}`)}
                  </span>
                </div>
              </Link>
            )}

            {/* Children */}
            {renderChildren(route)}
          </li>
        );
      })}
    </ul>
  );
};

export default SidebarLinks;
